import classNames from 'classnames';
import React, { FC, useEffect, useMemo, useState } from 'react';
import css from './VendorLogo.module.css';

const FALLBACK_VENDOR = 'Test-Source';
const buildVendorPath = (vendorName: string) =>
  `../../../assets/vendor/${vendorName}.svg`;

const vendorLogoLoaders = import.meta.glob('../../../assets/vendor/*.svg', {
  import: 'default'
}) as Record<string, () => Promise<string>>;

const vendorLogoLoadersByLowerName = new Map<string, () => Promise<string>>(
  Object.entries(vendorLogoLoaders)
    .map(([path, loader]) => {
      const match = path.match(/\/([^/]+)\.svg$/);
      return match ? [match[1].toLowerCase(), loader] : null;
    })
    .filter((entry): entry is [string, () => Promise<string>] => Boolean(entry))
);

const vendorLogoUrlCache = new Map<string, string>();

const toCacheKey = (vendorName: string) => vendorName.toLowerCase();

const getVendorLogoLoader = (vendorName: string) => {
  const exactLoader = vendorLogoLoaders[buildVendorPath(vendorName)];
  if (exactLoader) {
    return exactLoader;
  }

  return vendorLogoLoadersByLowerName.get(vendorName.toLowerCase());
};

const resolveVendorLogoUrl = async (vendorName: string) => {
  const cacheKey = toCacheKey(vendorName);
  const cached = vendorLogoUrlCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const primaryLoader = getVendorLogoLoader(vendorName);
  if (primaryLoader) {
    const primary = await primaryLoader();
    vendorLogoUrlCache.set(cacheKey, primary);
    return primary;
  }

  const fallbackCacheKey = toCacheKey(FALLBACK_VENDOR);
  const fallbackCached = vendorLogoUrlCache.get(fallbackCacheKey);
  if (fallbackCached) {
    return fallbackCached;
  }

  const fallbackLoader = getVendorLogoLoader(FALLBACK_VENDOR);
  if (fallbackLoader) {
    const fallback = await fallbackLoader();
    vendorLogoUrlCache.set(fallbackCacheKey, fallback);
    return fallback;
  }

  return '';
};

interface VendorLogoProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  vendor: string;
}

export const VendorLogo: FC<VendorLogoProps> = ({
  className,
  vendor,
  ...rest
}) => {
  const { alt, onError, ...imgProps } = rest;

  const normalizedVendor = useMemo(() => {
    const raw = String(vendor || '').trim();
    // Defensive fallback: malformed values can surface from upstream props.
    if (!raw || raw.includes('-module-')) {
      return FALLBACK_VENDOR;
    }
    return raw.replace(/\s+/g, '-');
  }, [vendor]);

  const [failed, setFailed] = useState(false);
  const [url, setUrl] = useState('');

  useEffect(() => {
    setFailed(false);
    setUrl('');
  }, [normalizedVendor]);

  useEffect(() => {
    let active = true;

    const loadLogo = async () => {
      const logoName = failed ? FALLBACK_VENDOR : normalizedVendor;

      try {
        const nextUrl = await resolveVendorLogoUrl(logoName);
        if (active) {
          setUrl(nextUrl);
        }
      } catch {
        if (active) {
          if (!failed) {
            setFailed(true);
          } else {
            setUrl('');
          }
        }
      }
    };

    void loadLogo();

    return () => {
      active = false;
    };
  }, [failed, normalizedVendor]);

  function handleError(event: React.SyntheticEvent<HTMLImageElement, Event>) {
    setFailed(true);
    onError?.(event);
  }

  if (!url) {
    return null;
  }

  return (
    <img
      {...imgProps}
      className={classNames(css.vendorIcon, className)}
      src={url}
      alt={alt || vendor}
      onError={handleError}
    />
  );
};
